import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
  withCredentials: false, // Don't send cookies
});

// Request interceptor to add auth token and tenant
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const tenant = localStorage.getItem('tenant');
    
    // Don't add Authorization header for login and refresh endpoints
    const isAuthEndpoint = config.url === '/auth/login' || config.url === '/auth/refresh';
    
    if (token && !isAuthEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Only add tenant from localStorage if not already set in request
    if (tenant && !config.headers['X-Tenant-Slug']) {
      config.headers['X-Tenant-Slug'] = tenant;
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    console.error('API Response error:', error);
    console.error('Error response:', error.response);
    
    const originalRequest = error.config;
    
    // Don't retry for login/refresh endpoints
    const isAuthEndpoint = originalRequest.url === '/auth/login' || originalRequest.url === '/auth/refresh';
    
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          
          const { access_token } = response.data;
          localStorage.setItem('token', access_token);
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tenant');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;

/**
 * Authenticated fetch wrapper that includes JWT token
 * Use this for pages that still use fetch instead of axios
 */
export const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const tenant = localStorage.getItem('tenant');
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  if (tenant) {
    headers['X-Tenant-Slug'] = tenant;
  }
  
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  // Handle 401 by redirecting to login
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tenant');
    window.location.href = '/login';
    throw new Error('Authentication required');
  }
  
  return response;
};